import React from 'react';
import { Table, Button, Space, Tag, Avatar, Tooltip } from 'antd';
import {
  EyeOutlined, MailOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import { format } from 'date-fns';

const ContactMessageTable = ({ submissions, onView }) => {
  if (!submissions || submissions.length === 0) {
    return null;
  }

  const getStatusConfig = (status) => {
    const configs = {
      'new': { color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', label: 'New' },
      'read': { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', label: 'Read' },
      'resolved': { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', label: 'Resolved' },
    };
    return configs[status] || configs['new'];
  };

  const columns = [
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Name</span>,
      key: 'name',
      render: (_, submission) => {
        const name = submission.fullName || 'Unknown';
        const firstLetter = name.charAt(0).toUpperCase();

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Avatar
              size={40}
              style={{
                backgroundColor: '#e6d5f0',
                color: '#470172',
                fontFamily: 'Rubik, sans-serif',
                fontSize: '18px',
                fontWeight: 500
              }}
            >
              {firstLetter}
            </Avatar>
            <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
              {name}
            </div>
          </div>
        );
      },
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Contact</span>,
      key: 'contact',
      responsive: ['md'],
      render: (_, submission) => (
        <div>
          <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 400, color: '#470172', marginBottom: '4px' }}>
            {submission.email}
          </div>
          <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 400, color: '#8b8b8b' }}>
            {submission.phone || 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Message</span>,
      key: 'message',
      responsive: ['lg'],
      render: (_, submission) => (
        <div style={{
          fontFamily: 'Rubik, sans-serif',
          fontSize: '14px',
          fontWeight: 400,
          color: '#595959',
          maxWidth: 280,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          whiteSpace: 'normal'
        }}>
          {submission.message}
        </div>
      ),
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Date</span>,
      key: 'date',
      responsive: ['lg'],
      render: (_, submission) => (
        <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '12px', fontWeight: 400, color: '#8b8b8b' }}>
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {submission.createdAt ? format(new Date(submission.createdAt), 'MMM d, yyyy') : '—'}
        </div>
      ),
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Status</span>,
      key: 'status',
      render: (_, submission) => {
        const config = getStatusConfig(submission.status);
        return (
          <Tag
            style={{
              fontSize: 13,
              fontFamily: "'Rubik', sans-serif",
              fontWeight: 600,
              border: `1px solid ${config.border}`,
              background: config.bg,
              color: config.color,
              padding: '4px 14px',
              borderRadius: 6,
            }}
          >
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Actions</span>,
      key: 'actions',
      align: 'right',
      render: (_, submission) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onView(submission)}
              style={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                color: '#470172',
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={submissions}
      rowKey="_id"
      scroll={{ x: 'max-content' }}
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => (
          <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
            Total {total} submissions
          </span>
        ),
        style: { fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400 }
      }}
      style={{ fontFamily: 'Rubik, sans-serif' }}
      locale={{
        emptyText: (
          <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
            No submissions found
          </span>
        )
      }}
      rowClassName={() => 'hover:bg-gray-50'}
    />
  );
};

export default ContactMessageTable;
